import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import formStyles from "../../features/MainForm/Form.module.css";
import styles from "../Input/Input.module.css";

interface FileUploadProps {
  id: string;
  label: string;
  maxSize?: number;
  onFileChange: (file: File | null) => void;
}

const ALLOWED_FILES = ".pdf,.docx,.jpg";
const mimeTypesMap: Record<string, string[]> = {
  ".pdf": ["application/pdf"],
  ".docx": [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  ".jpg": ["image/jpeg"],
  ".jpeg": ["image/jpeg"],
};

export const InputFileField: React.FC<FileUploadProps> = ({
  id,
  label,
  maxSize = 5,
  onFileChange,
}) => {
  const { t } = useTranslation();
  const [fileState, setFileState] = useState({
    errorMessage: null as string | null,
    labelText: t("field.attachment.placeholder"),
    hasFile: false,
  });

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize * 1024 * 1024) {
      return t("field.attachment.maxSize", { maxSize });
    }
    const isValidType = ALLOWED_FILES.split(",").some((ext) =>
      mimeTypesMap[ext]?.includes(file.type)
    );
    if (!isValidType) {
      return t("field.attachment.valid", { fileType: ALLOWED_FILES });
    }
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileState({
        errorMessage: null,
        labelText: t("field.attachment.placeholder"),
        hasFile: false,
      });
      onFileChange(null);
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setFileState({
        errorMessage: validationError,
        labelText: t("field.attachment.placeholder"),
        hasFile: false,
      });
      onFileChange(null);
    } else {
      setFileState({ errorMessage: null, labelText: file.name, hasFile: true });
      onFileChange(file);
    }
  };

  return (
    <div className={formStyles.formGroup}>
      <label htmlFor={id} className={formStyles.labelFile}>
        {label}
      </label>
      <style>{`
        .inputFileField::after {
          content: "${fileState.labelText}";
        }
      `}</style>
      <input
        id={id}
        type="file"
        accept={ALLOWED_FILES}
        onChange={handleFileChange}
        className={classNames("inputFileField", styles.inputFileField, {
          [styles.onInputFileFieldStart]: !fileState.hasFile,
          [styles.onInputFileFieldEnd]: fileState.hasFile,
          [styles.inputFileFieldError]: fileState.errorMessage,
        })}
      />
      <p className={classNames(styles.labelMuted, styles.mt8)}>
        {t("field.attachment.label_1", { allowedFormats: ALLOWED_FILES })}
      </p>
      <p className={styles.labelMuted}>
        {t("field.attachment.label_2", { maxSize })}
      </p>

      {fileState.errorMessage && (
        <p className={formStyles.formError}>{fileState.errorMessage}</p>
      )}
    </div>
  );
};
